import React from 'react'
import { Clock, Building2, Ship } from 'lucide-react'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente: ExpedienteDespacho
  compacto?: boolean
}

export interface CalculoDiasLibres {
  diasTranscurridos: number
  diasLibresTerminal: number
  diasRestantesTerminal: number
  almacenajeExcedido: boolean
  costoAlmacenajeMXN: number
  diasLibresContenedor: number
  diasRestantesContenedor: number
  demorasExcedidas: boolean
  costoDemorasUSD: number
  tarifaDiariaDemoraUSD: number
  tarifaDiariaAlmacenajeMXN: number
}

export function calcularDiasLibresYDemoras(expediente: ExpedienteDespacho): CalculoDiasLibres {
  // Determinamos días transcurridos simulados en base al ID o fecha
  const seed = (expediente.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 9) + 2
  const diasTranscurridos = seed

  // Reglas Art. 15 Frac. V Ley Aduanera (SSA Marine Manzanillo)
  const esImportacion = expediente.tipoOperacion === 'IMPORTACION'
  const diasLibresTerminal = esImportacion ? 7 : 15 // 7 días imp / 15 días exp
  const diasRestantesTerminal = Math.max(0, diasLibresTerminal - diasTranscurridos)
  const almacenajeExcedido = diasTranscurridos > diasLibresTerminal
  const tarifaDiariaAlmacenajeMXN = expediente.contenedor.tipo === '40HC' || expediente.contenedor.tipo === '40RF' ? 2450 : 1650
  const costoAlmacenajeMXN = almacenajeExcedido
    ? (diasTranscurridos - diasLibresTerminal) * tarifaDiariaAlmacenajeMXN
    : 0

  // Reglas Comerciales Naviera (Maersk, MSC, CMA CGM)
  const diasLibresContenedor = 12 // Estándar naviero en puerto mexicano
  const diasRestantesContenedor = Math.max(0, diasLibresContenedor - diasTranscurridos)
  const demorasExcedidas = diasTranscurridos > diasLibresContenedor
  const tarifaDiariaDemoraUSD = expediente.contenedor.tipo === '40RF' ? 240 : 180 // Reefer es más costoso
  const costoDemorasUSD = demorasExcedidas
    ? (diasTranscurridos - diasLibresContenedor) * tarifaDiariaDemoraUSD
    : 0

  return {
    diasTranscurridos,
    diasLibresTerminal,
    diasRestantesTerminal,
    almacenajeExcedido,
    costoAlmacenajeMXN,
    diasLibresContenedor,
    diasRestantesContenedor,
    demorasExcedidas,
    costoDemorasUSD,
    tarifaDiariaDemoraUSD,
    tarifaDiariaAlmacenajeMXN
  }
}

export const IndicadorDiasLibres: React.FC<Props> = ({ expediente, compacto = true }) => {
  const calculo = calcularDiasLibresYDemoras(expediente)

  // Porcentajes de uso de tiempo libre
  const pctTerminal = Math.min(100, Math.round((calculo.diasTranscurridos / calculo.diasLibresTerminal) * 100))
  const pctContenedor = Math.min(100, Math.round((calculo.diasTranscurridos / calculo.diasLibresContenedor) * 100))

  const colorTerminal =
    pctTerminal > 90 ? 'text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/30' :
    pctTerminal > 60 ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/30' :
    'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30'

  const colorContenedor =
    pctContenedor > 90 ? 'text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/30' :
    pctContenedor > 60 ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/30' :
    'text-blue-600 dark:text-blue-400 bg-blue-500/10 border-blue-500/30'

  if (compacto) {
    return (
      <div className="flex flex-wrap items-center gap-2 mt-2">
        {/* Almacenaje Terminal */}
        <div
          title={`Almacenaje Terminal: ${calculo.diasTranscurridos} de ${calculo.diasLibresTerminal} días libres utilizados (Art. 15 Frac. V Ley Aduanera)`}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-bold ${colorTerminal}`}
        >
          <Building2 className="w-3 h-3" />
          <span>
            Almacenaje SSA: Día <strong>{calculo.diasTranscurridos}</strong> de {calculo.diasLibresTerminal} libres
          </span>
          {calculo.almacenajeExcedido ? (
            <span className="font-mono text-rose-500 font-black">
              (+${calculo.costoAlmacenajeMXN.toLocaleString('es-MX')} MXN)
            </span>
          ) : (
            <span className="text-[10px] opacity-75 font-normal">
              ({calculo.diasRestantesTerminal}d restantes)
            </span>
          )}
        </div>

        {/* Demoras Naviera */}
        <div
          title={`Demoras de Contenedor (${expediente.contenedor.lineaNaviera}): ${calculo.diasTranscurridos} de ${calculo.diasLibresContenedor} días libres antes de incurrir en demurrage`}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-bold ${colorContenedor}`}
        >
          <Ship className="w-3 h-3" />
          <span>
            Demoras {expediente.contenedor.lineaNaviera}: Día <strong>{calculo.diasTranscurridos}</strong> de {calculo.diasLibresContenedor} libres
          </span>
          {calculo.demorasExcedidas ? (
            <span className="font-mono text-rose-500 font-black">
              (+${calculo.costoDemorasUSD.toLocaleString()} USD)
            </span>
          ) : (
            <span className="text-[10px] opacity-75 font-normal">
              ($0 USD demurrage)
            </span>
          )}
        </div>
      </div>
    )
  }

  // Vista Expandida para Dossier / Detalle
  return (
    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-zinc-300 flex items-center gap-2">
          <Clock className="w-4 h-4 text-cyan-500" />
          Cronómetro de Tiempos Portuarios y Días Libres
        </h4>
        <span className="text-[10px] text-slate-500 dark:text-zinc-400 font-medium">
          Día {calculo.diasTranscurridos} en puerto
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Terminal SSA Almacenaje */}
        <div className="p-3 rounded-xl bg-white dark:bg-zinc-900 border border-slate-200/60 dark:border-white/5 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-slate-800 dark:text-zinc-200 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5 text-emerald-500" />
              Almacenaje en Terminal (SSA Manzanillo)
            </span>
            <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${colorTerminal}`}>
              {calculo.diasRestantesTerminal > 0 ? `${calculo.diasRestantesTerminal}d libres` : 'Excedido'}
            </span>
          </div>

          {/* Barra de progreso */}
          <div className="w-full bg-slate-100 dark:bg-zinc-800 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                pctTerminal > 90 ? 'bg-rose-500' : pctTerminal > 60 ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              style={{ width: `${pctTerminal}%` }}
            />
          </div>

          <div className="flex justify-between text-[11px] text-slate-500 dark:text-zinc-400">
            <span>Día {calculo.diasTranscurridos} de {calculo.diasLibresTerminal} permitidos</span>
            <span className="font-mono">
              {calculo.almacenajeExcedido
                ? `Costo: $${calculo.costoAlmacenajeMXN.toLocaleString('es-MX')} MXN`
                : 'Sin costo de almacenaje'}
            </span>
          </div>
          <p className="text-[10px] text-slate-400 dark:text-zinc-500">
            Fundamento: Art. 15 Frac. V Ley Aduanera ({expediente.tipoOperacion === 'IMPORTACION' ? '7 días importación' : '15 días exportación'}).
          </p>
        </div>

        {/* Demurrage Naviera */}
        <div className="p-3 rounded-xl bg-white dark:bg-zinc-900 border border-slate-200/60 dark:border-white/5 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-slate-800 dark:text-zinc-200 flex items-center gap-1.5">
              <Ship className="w-3.5 h-3.5 text-blue-500" />
              Demoras de Contenedor ({expediente.contenedor.lineaNaviera})
            </span>
            <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${colorContenedor}`}>
              {calculo.diasRestantesContenedor > 0 ? `${calculo.diasRestantesContenedor}d libres` : 'En Demurrage'}
            </span>
          </div>

          {/* Barra de progreso */}
          <div className="w-full bg-slate-100 dark:bg-zinc-800 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                pctContenedor > 90 ? 'bg-rose-500' : pctContenedor > 60 ? 'bg-amber-500' : 'bg-blue-500'
              }`}
              style={{ width: `${pctContenedor}%` }}
            />
          </div>

          <div className="flex justify-between text-[11px] text-slate-500 dark:text-zinc-400">
            <span>Día {calculo.diasTranscurridos} de {calculo.diasLibresContenedor} libres naviera</span>
            <span className="font-mono">
              {calculo.demorasExcedidas
                ? `Demora: $${calculo.costoDemorasUSD.toLocaleString()} USD`
                : '$0 USD acumulados'}
            </span>
          </div>
          <p className="text-[10px] text-slate-400 dark:text-zinc-500">
            Tarifa naviera estándar: ${calculo.tarifaDiariaDemoraUSD} USD/día tras vencer el periodo libre.
          </p>
        </div>
      </div>
    </div>
  )
}

export default IndicadorDiasLibres
